import {
  CULTURE_GREAT_BRITAIN,
  CULTURE_NORWAY,
  CURRENCY_EUR,
  CURRENCY_GBP,
  CURRENCY_NOK,
  ECONOMY_CABIN,
  FALLBACK_CULTURE,
  MAX_CONSECUTIVE_DECLINED_PAYMENTS,
  PaymentCodes,
  PRIMARY_CONTACT_TYPE_CODE,
  FALLBACK_CURRENCY,
  COUNTRY_CODE_US,
  CURRENCY_USD,
  CULTURE_FRENCH,
} from '@constants/app.constants';
import { CabinProductClass } from '@constants/cabin.constants';
import {
  MealFirstServiceSsrEnum,
  MealSecondServiceSsrEnum,
  MealServiceType,
  MealsSsrEnum,
} from '@constants/meals.constants';
import {
  Passenger,
  PassengerSeat,
  PassengerTypeCode,
  Segment,
  SegmentWithJourneyKey,
  JourneyBags,
  MealService,
  PassengerSsr,
  PassengerWithMealsCountBySsr,
  PassengerWithExtrasCountBySsr,
  Meals,
  MealsCostAndCountBySsr,
  ExtrasCostAndCountBySsr,
  BagsSsrEnum,
  BagsCostAndCountBySsr,
  CostAndCount,
  SegmentFare,
  PassengerFare,
  TaxTypeCode,
  Name,
  N0CategoryDetail,
  BookingPaymentStatus,
  N0JourneyDisplayStatus,
  Payment,
  Leg,
  JourneySummary,
  N0JourneyBreakdown,
  N0CategoryBreakdown,
} from '@models/booking.models';
import { ChargeType, ServiceCharge } from '@models/requests/passenger-update-request.model';
import { createSelector } from '@ngrx/store';
import { selectBookingFeatureState } from '../reducers';
import { isEqual } from 'lodash-es';
import { ExtrasSsrsEnum } from '@constants/extras.constants';
import { CanSellMeals } from '@models/cms/meals.model';
import { selectSelectedCurrencyCode } from './culture.selector';
import { CanCheckIn } from '@models/app.models';
import { LiftStatusEnum } from '@models/check-in.models';
import { selectIsCheckInPath } from './router.selector';
import { DateTime } from 'luxon';
import { parsePhoneNumberToParts } from '@utils/data-utils';
import { selectPhoneCodes } from './resource.selector';
import { selectExtrasCopy } from "@selectors/cms.selector";
import { addOldSsrPriceWithLabel, addSsrBundleWithStrikethroughPrices, tryParseOldSsrPrices, tryParseSsrBundles } from "@utils/ssrs.utils";
import { N0SsrAvailability } from "@models/responses/ssr-availability-response.model";
import { tryParseSaveOnlineLabels } from "@utils/extras-utils";
import { BookingType } from '@constants/booking.constants';

/* istanbul ignore next  */
export const selectBooking = createSelector(
  selectBookingFeatureState,
  (state) => state.booking,
);

/* istanbul ignore next  */
export const selectOriginalBooking = createSelector(
  selectBookingFeatureState,
  (state) => state.originalBooking,
);

export const selectBookingCurrency = createSelector(
  selectBooking,
  (booking) => booking?.currencyCode,
);

export const selectBookingInfo = createSelector(
  selectBooking,
  (booking) => booking?.info,
);

export const selectBookingRecordLocator = createSelector(
  selectBooking,
  (booking) => booking?.recordLocator,
);

export const selectBookingSsrAvailability = createSelector(
  selectBookingFeatureState,
  selectBookingCurrency,
  selectExtrasCopy,
  (state, currencyCode, extrasCopy) : N0SsrAvailability | null => {
    if (!state.ssrAvailability) {
      return null;
    }

    const oldSsrPrices = tryParseOldSsrPrices(extrasCopy?.fastTrackPrices);
    const saveOnlineLabels = tryParseSaveOnlineLabels(extrasCopy?.saveOnlineLabels);
    const ssrBundles = tryParseSsrBundles(extrasCopy?.ssrBundle);

    let updatedJourneySsrs = state.ssrAvailability.journeySsrs;

    if (oldSsrPrices && saveOnlineLabels) {
      updatedJourneySsrs = updatedJourneySsrs.map(journeySsr =>
        addOldSsrPriceWithLabel({
          journeySsr,
          oldSsrPrices: oldSsrPrices,
          saveOnlineLabels,
          currency: currencyCode ?? FALLBACK_CURRENCY
        })
      );
    }

    if (ssrBundles) {
      updatedJourneySsrs = updatedJourneySsrs.map(journeySsr =>
        addSsrBundleWithStrikethroughPrices({
          journeySsr,
          ssrBundlesCopy: ssrBundles,
        })
      );
    }

    return {
      ...state.ssrAvailability,
      journeySsrs: updatedJourneySsrs
    };
  },
);

/* istanbul ignore next  */
export const selectBookingCheckInPassengers = createSelector(
  selectBookingFeatureState,
  (state) => state.checkInPassengers,
);

/* istanbul ignore next  */
export const selectBookingTravelersToChange = createSelector(
  selectBookingFeatureState,
  (state) => state.travelersToChange,
);

/* istanbul ignore next  */
export const selectTokenGetBooking = createSelector(
  selectBookingFeatureState,
  (state) => state.tokenGetBooking,
);

/* istanbul ignore next  */
export const selectBookingCheckInStatus = createSelector(
  selectBookingFeatureState,
  (state) => state.checkInStatus,
);

/* istanbul ignore next  */
export const selectBookingCheckIn = createSelector(
  selectBookingFeatureState,
  (state) => state.checkIn,
);

export const selectPromotionCode = createSelector(
  selectBooking,
  (state) => state?.typeOfSale?.promotionCode ?? '',
);

/* istanbul ignore next  */
export const selectBookingProcessing = createSelector(
  selectBookingFeatureState,
  (state) => state.processing,
);

/* istanbul ignore next  */
export const selectBookingAvailableMealsSsr = createSelector(
  selectBookingSsrAvailability,
  (state) => {
    let result: { ssrCode: MealsSsrEnum; name: string }[] = [];
    state?.legsSsrs?.forEach((s) => {
      result = result.concat(
        s.mealSsrs.map((s) => ({ ssrCode: s.ssrCode, name: s.name })),
      );
    });
    return result;
  },
);

/* istanbul ignore next  */
export const selectBookingAvailableExtrasSsr = createSelector(
  selectBookingSsrAvailability,
  (state) => {
    let result: { ssrCode: string; name: string }[] = [];
    state?.journeySsrs?.forEach((s) => {
      result = result.concat(
        s.extrasSsrs.map((s) => ({ ssrCode: s.ssrCode, name: s.name })),
      );
    });
    return result;
  },
);

export const selectPassengers = createSelector(
  selectBooking,
  (booking) => booking?.passengers,
);

export const selectNonInfantPassengers = createSelector(
  selectPassengers,
  (passengers) => {
    const newPassengers = { ...passengers };
    Object.keys(newPassengers).forEach((paxKey) => {
      if (newPassengers[paxKey]?.passengerTypeCode === PassengerTypeCode.INF) {
        delete newPassengers[paxKey];
      }
    });
    return newPassengers;
  },
);

export const selectNonInfantPassengersCount = createSelector(
  selectNonInfantPassengers,
  (passengers) => {
    return Object.values(passengers).length;
  },
);

export const selectPassengersList = createSelector(
  selectPassengers,
  (passengers) => passengers && Object.values(passengers),
);

export const selectPassengersForCheckin = createSelector(
  selectPassengersList,
  (passengers) => passengers?.reduce((prev, acc) => ({...prev, [acc.passengerKey]: true}), {})
)

/* istanbul ignore next  */
export const selectCurrentlyActiveJourneyKey = createSelector(
  selectBookingFeatureState,
  (state) => state.currentlyActiveJourneyKey,
);

/* istanbul ignore next  */
export const selectBookingDangerousGoodsViewed = createSelector(
  selectBookingFeatureState,
  (state) => state.dangerousGoodsViewed,
);

export const selectCurrentlyActiveJourney = createSelector(
  selectBooking,
  selectCurrentlyActiveJourneyKey,
  (booking, journeyKey) =>
    booking?.journeys.find((j) => j.journeyKey === journeyKey),
);

export const selectJourneys = createSelector(
  selectBooking,
  (booking) => booking?.journeys,
);

export const selectActiveJourneys = createSelector(selectBooking, (b) => {
  const activeJourneyKeys = b?.details?.journeyDetail
    ?.filter((jd) => !jd.isFlown && !jd.isCanceled)
    .map((jd) => jd.journeyKey);
  return b?.journeys?.filter((j) => activeJourneyKeys?.includes(j.journeyKey));
});

export const selectSegments = createSelector(
  selectJourneys,
  (js) => js?.reduce((s, j) => s.concat(j.segments), [] as Segment[]),
);

export const selectActiveSegments = createSelector(
  selectActiveJourneys,
  (ajs) => ajs?.reduce((s, j) => s.concat(j.segments), [] as Segment[]),
);

export const selectActiveLegs = createSelector(
  selectActiveSegments,
  (sgms) => sgms?.reduce((ls, s) => ls.concat(s.legs), [] as Leg[]),
);

export const selectSegmentsWithJourneyKeys = createSelector(
  selectJourneys,
  (journeys) =>
    journeys?.reduce((segments, journey) => {
      return segments.concat(
        journey.segments.map(
          (seg) =>
            ({
              ...seg,
              journeyKey: journey.journeyKey,
            }) as SegmentWithJourneyKey,
        ),
      );
    }, [] as SegmentWithJourneyKey[]),
);

export const selectBookingBreakdown = createSelector(
  selectBooking,
  (booking) => booking?.breakdown,
);

export const selectBookingTripBreakdown = createSelector(
  selectBooking, (booking) => booking?.tripBreakdown,
);

export const selectBookingJourneyBreakdowns = createSelector(
  selectBookingTripBreakdown,
  (breakdown) => breakdown?.journeyBreakdowns,
);

export const selectBookingKey = createSelector(
  selectBooking,
  (booking) => booking?.bookingKey,
);

export const selectBookingDetails = createSelector(
  selectBooking,
  (booking) => booking?.details,
);

export const selectBookingJourneys = createSelector(
  selectBooking,
  (booking) => booking?.journeys,
);

export const selectCanCancel = createSelector(
  selectBookingDetails,
  (d) => d?.canCancel ?? true,
);

export const selectJourneyDetails = createSelector(
  selectBookingDetails,
  (bookingDetails) => bookingDetails?.journeyDetail ?? [],
);

export const selectIsAnyFlightsFlown = createSelector(
  selectBookingDetails,
  (bookingDetails) =>
    bookingDetails?.journeyDetail?.some((jd) => jd.isFlown) ?? false,
);

export const selectModifiableJourneyJourneys = createSelector(
  selectJourneyDetails,
  selectJourneys,
  (jd, js) => {
    const jks = jd.filter((d) => d.canModifyJourney).map((fj) => fj.journeyKey);
    return js?.filter((j) => jks.includes(j.journeyKey));
  },
);

export const selectModifiableFlightJourneys = createSelector(
  selectJourneyDetails,
  selectJourneys,
  (jd, js) => {
    const jks = jd.filter((d) => d.canModifyFlight).map((fj) => fj.journeyKey);
    return js?.filter((j) => jks.includes(j.journeyKey));
  },
);

export const selectModifiableEconomyClassFlightJourneys = createSelector(
  selectJourneyDetails,
  selectJourneys,
  (jd, js) => {
    const jks = jd
      .filter((d) => d.canModifyFlight && !d.isCanceled)
      .map((fj) => fj.journeyKey);
    return js?.filter(
      (j) => jks.includes(j.journeyKey) && j.cabin === ECONOMY_CABIN,
    );
  },
);

export const selectEconomyJourneys = createSelector(
  selectJourneys,
  (journey) => journey?.filter(j => j.cabin === ECONOMY_CABIN)
)

export const selectJourneysInCheckInWindow = createSelector(
  selectJourneyDetails,
  selectJourneys,
  (jd, js) => {
    const jks = jd.filter((d) => d.inCheckinWindow).map((fj) => fj.journeyKey);
    return js?.filter((j) => jks.includes(j.journeyKey));
  },
);

export const selectJourneysWithin24Hours = createSelector(
  selectJourneyDetails,
  selectJourneys,
  (jd, js) => {
    const jks = jd.filter((d) => d.isWithin24hours).map((fj) => fj.journeyKey);
    return js?.filter((j) => jks.includes(j.journeyKey)) ?? [];
  },
);

export const selectBookingSegmentsThatCanSellSeats = createSelector(
  selectSegmentsWithJourneyKeys,
  selectBookingDetails,
  selectJourneysInCheckInWindow,
  selectIsCheckInPath,
  (segments, details, checkInJourneys, isCheckInPath) =>
    segments?.filter((seg) => {
      return (
        details?.journeyDetail.some(
          (jd) =>
            jd.journeyKey === seg.journeyKey && jd.canSellSeats && !jd.isFlown,
        ) &&
        (isCheckInPath
          ? !!checkInJourneys?.some((j) => j.journeyKey === seg.journeyKey)
          : true)
      );
    }),
);

export const selectBookingLegs = createSelector(
  selectSegmentsWithJourneyKeys,
  selectBookingDetails,
  selectJourneysInCheckInWindow,
  selectIsCheckInPath,
  (segments, details, checkInJourneys, isCheckInPath) =>
    segments?.filter((seg) => {
    return (
      details?.journeyDetail.some(
        (jd) =>
          jd.journeyKey === seg.journeyKey && !jd.isFlown,
      ) &&
      (isCheckInPath
        ? !!checkInJourneys?.some((j) => j.journeyKey === seg.journeyKey)
        : true)
    );
  }).reduce((ls, s) => ls.concat(s.legs), [] as Leg[]),
);

export const selectIsAnyJourneyInCheckInWindow = createSelector(
  selectJourneyDetails,
  (details) => {
    return !!details?.some((j) => j.inCheckinWindow);
  },
);

export const selectCanAnyJourneyCheckIn = createSelector(
  selectJourneyDetails,
  (details) => {
    return !!details?.some((j) => j.canCheckin);
  },
);

export const selectIsCheckInAvailable = createSelector(
  selectJourneyDetails,
  selectBookingCheckInStatus,
  (details, checkInStatus) => {
    return !!details?.some(
      (j) =>
        j.canCheckin &&
        !!checkInStatus?.some(
          (s) =>
            s.journeyKey === j.journeyKey &&
            Object.keys(s.passengers).some(
              (p) =>
                s.passengers[p]?.some(
                  (status) => status.status === LiftStatusEnum.DEFAULT,
                ),
            ),
        ),
    );
  },
);

export const selectIsAnyTravelerCheckedIn = createSelector(
  selectJourneyDetails,
  selectBookingCheckInStatus,
  (details, checkInStatus) => {
    return !!details?.some(
      (j) =>
        j.canShowBoardingPass &&
        !!checkInStatus?.some(
          (s) =>
            s.journeyKey === j.journeyKey &&
            Object.keys(s.passengers).some(
              (p) =>
                s.passengers[p]?.some(
                  (status) =>
                    status.status === LiftStatusEnum.CHECKED_IN ||
                    status.status === LiftStatusEnum.BOARDED,
                ),
            ),
        ),
    );
  },
);

export const selectHaveAllFlightsFlown = createSelector(
  selectJourneyDetails,
  (journeyDetails) => journeyDetails.every((j) => j.isFlown),
);

export const selectAreAllRemainingFlightsCancelled = createSelector(
  selectJourneyDetails,
  (journeyDetails) => {
    return journeyDetails
      .filter((jd) => !jd.isFlown)
      .every((jd) => jd.isCanceled);
  },
);

export const selectIsAnyRemainingFlightCancelled = createSelector(
  selectJourneyDetails,
  (journeyDetails) => {
    return journeyDetails
      .filter((jd) => !jd.isFlown)
      .some((jd) => jd.isCanceled);
  },
);

export const selectIsCheckInAvailableByJourney = createSelector(
  selectJourneyDetails,
  (details) => {
    const result: CanCheckIn = {};

    details?.forEach((j) => {
      result[j.journeyKey] = j.canCheckin;
    });

    return result;
  },
);

export const selectBookingStatusByJourney = createSelector(
  selectJourneyDetails,
  (details) => {
    const result: { [journeyKey: string]: N0JourneyDisplayStatus } = {};

    details?.forEach((j) => {
      result[j.journeyKey] = j.journeyDisplayStatus;
    });

    return result;
  },
);

export const selectAnyJourneyCanSellStatuses = createSelector(
  selectJourneyDetails,
  (journeyDetails) => {
    if (journeyDetails && journeyDetails.length) {
      return {
        canSellBags: journeyDetails.some((j) => j.canSellBags),
        canSellSeats: journeyDetails.some((j) => j.canSellSeats),
        canSellMeals: journeyDetails.some((j) => j.canSellMeals),
        canSellExtras: journeyDetails.some((j) => j.canSellExtras),
        restrictBagsToCheckin: !journeyDetails.some(
          (j) => !j.restrictBagsToCheckin,
        ),
      };
    }

    return;
  },
);

export const selectCanSellMeals = createSelector(
  selectJourneys,
  selectJourneyDetails,
  (journeys, journeyDetails) => {
    const result: CanSellMeals = {};
    if (
      journeyDetails &&
      journeyDetails.length &&
      journeys &&
      journeys.length
    ) {
      journeys.forEach((j) => {
        j.segments.forEach((s) => {
          s.legs.forEach((l) => {
            result[l.legKey] =
              journeyDetails.find((jd) => jd.journeyKey === j.journeyKey)
                ?.canSellMeals ?? false;
          });
        });
      });
    }
    return result;
  },
);

export const selectBookingPassengersByType = createSelector(
  selectPassengers,
  (passengers) => {
    return Object.values(passengers ?? {}).reduce(
      (passListByType, pass) => {
        if (!pass.passengerTypeCode) {
          return passListByType;
        }
        let passForType = passListByType.find(
          (list) => list.type === pass.passengerTypeCode,
        );
        if (!passForType) {
          passForType = {
            type: pass.passengerTypeCode,
            passengers: [],
          };
          passListByType.push(passForType);
        }
        passForType.passengers.push(pass);
        return passListByType;
      },
      [] as {
        type: PassengerTypeCode;
        passengers: Passenger[];
      }[],
    );
  },
);

export const selectConfirmationNumber = createSelector(
  selectBooking,
  (booking) => booking?.recordLocator,
);

export const selectOriginFullName = createSelector(
  selectBooking,
  (booking) => booking?.journeys?.[0]?.designator?.origin,
);

export const selectDestinationFullName = createSelector(
  selectBooking,
  (booking) => booking?.journeys?.[0]?.designator?.destination,
);

export const selectEarliestDepartureDate = createSelector(
  selectBooking,
  (booking) =>
    booking?.journeys?.reduce((earliestJourneyDeparture, journey) => {
      const thisJourneyEarliestDeparture = journey.segments.reduce(
        (earliestSegmentDeparture, segment) => {
          const departure = new Date(segment.designator.departure);
          return departure < earliestSegmentDeparture
            ? departure
            : earliestSegmentDeparture;
        },
        new Date(8640000000000000),
      );
      return thisJourneyEarliestDeparture < earliestJourneyDeparture
        ? thisJourneyEarliestDeparture
        : earliestJourneyDeparture;
    }, new Date(8640000000000000)), // https://stackoverflow.com/questions/28425884/javascript-date-max-value/28425951
);

export const selectLatestDepartureDate = createSelector(
  selectBooking,
  (booking) =>
    booking?.journeys?.reduce((latestJourneyDeparture, journey) => {
      const thisJourneyLatestDeparture = journey.segments.reduce(
        (latestSegmentDeparture, segment) => {
          const departureDate = new Date(segment.designator.departure);
          return departureDate > latestSegmentDeparture
            ? departureDate
            : latestSegmentDeparture;
        },
        new Date(-8640000000000000),
      );
      return thisJourneyLatestDeparture > latestJourneyDeparture
        ? thisJourneyLatestDeparture
        : latestJourneyDeparture;
    }, new Date(-8640000000000000)), // https://stackoverflow.com/questions/28425884/javascript-date-max-value/28425951
);

export const selectEarliestJourney = createSelector(
  selectJourneys,
  selectEarliestDepartureDate,
  (journeys, earliestDepartureDate) =>
    journeys?.find((j) =>
      j.segments.some(
        (s) =>
          new Date(s.designator.departure).valueOf() ===
          earliestDepartureDate?.valueOf(),
      ),
    ),
);

export const selectBookingContactsList = createSelector(
  selectBooking,
  (booking) => Object.values(booking?.contacts ?? {}),
);

export const selectBookingPrimaryContact = createSelector(
  selectBooking,
  (booking) =>
    Object.values(booking?.contacts ?? {}).find(
      (c) => c.contactTypeCode === PRIMARY_CONTACT_TYPE_CODE,
    ),
);

export const selectBookingPrimaryContactPhoneNumber = createSelector(
  selectBookingPrimaryContact,
  (primaryContact) => primaryContact?.phoneNumbers?.[0]?.number,
);

export const selectBookingPrimaryContactPhoneNumberWithoutCountryCode = createSelector(
  selectBookingPrimaryContact,
  (primaryContact) => parsePhoneNumberToParts(primaryContact?.phoneNumbers?.[0]?.number ?? '').number
);

export const selectBookingPrimaryContactPhoneNumberCountryCode = createSelector(
  selectBookingPrimaryContact,
  selectPhoneCodes,
  (primaryContact, phoneCodes) => {
    const code = parsePhoneNumberToParts(
      primaryContact?.phoneNumbers?.[0]?.number ?? '',
    ).code;
    return phoneCodes?.find((phoneCode) => {
      return phoneCode.value === code;
    })?.countryCode;
  },
);

export const selectBookingPrimaryContactCultureCode = createSelector(
  selectBookingPrimaryContact,
  (primaryContact) => primaryContact?.cultureCode ?? FALLBACK_CULTURE,
);

export const selectBookingPassengerCount = createSelector(
  selectPassengers,
  (passengers) => {
    if (!passengers) {
      return 0;
    }
    return Object.keys(passengers).length;
  },
);

export const selectBookingAdultCountCarTrawler = createSelector(
  selectPassengers,
  (passengers) => {
    if (!passengers) {
      return 0;
    }
    return Object.values(passengers).reduce((adultsCount, pax) => {
      if (!pax.info.dateOfBirth) {
        return adultsCount;
      }
      const age = DateTime.now()
        .diff(DateTime.fromISO(pax.info.dateOfBirth))
        .shiftTo('years').years;

      if (age >= 16) {
        return adultsCount + 1;
      }
      return adultsCount;
    }, 0);
  },
);

export const selectBookingTeenagerCountCarTrawler = createSelector(
  selectPassengers,
  (passengers) => {
    if (!passengers) {
      return 0;
    }
    return Object.values(passengers).reduce((teensCount, pax) => {
      if (!pax.info.dateOfBirth) {
        return teensCount;
      }
      const age = DateTime.now()
        .diff(DateTime.fromISO(pax.info.dateOfBirth))
        .shiftTo('years').years;
      if (age >= 12 && age < 16) {
        return teensCount + 1;
      }
      return teensCount;
    }, 0);
  },
);

export const selectBookingChildCountCarTrawler = createSelector(
  selectPassengers,
  (passengers) => {
    if (!passengers) {
      return 0;
    }
    return Object.values(passengers).reduce((childrenCount, pax) => {
      if (!pax.info.dateOfBirth) {
        return childrenCount;
      }
      const age = DateTime.now()
        .diff(DateTime.fromISO(pax.info.dateOfBirth))
        .shiftTo('years').years;
      if (age >= 2 && age < 12) {
        return childrenCount + 1;
      }
      return childrenCount;
    }, 0);
  },
);

export const selectBookingInfantCountCarTrawler = createSelector(
  selectPassengers,
  (passengers) => {
    if (!passengers) {
      return 0;
    }
    return Object.values(passengers).reduce((infantsCount, pax) => {
      if (!pax.info.dateOfBirth) {
        return pax.infant ? infantsCount + 1 : infantsCount;
      }
      const age = DateTime.now()
        .diff(DateTime.fromISO(pax.info.dateOfBirth))
        .shiftTo('years').years;
      // technically the age should never be in this range,
      // they should always be assigned to an adult pax
      if ((age < 2 && age >= 0) || pax.infant) {
        return infantsCount + 1;
      }
      return infantsCount;
    }, 0);
  },
);

export const selectHidePrimaryContact = createSelector(
  selectBooking,
  (booking) => {
    if (!booking) {
      return true;
    }
    let hideContact = true;
    const primaryContactName = Object.values(booking.contacts ?? {}).find(
      (c) => c.contactTypeCode === PRIMARY_CONTACT_TYPE_CODE,
    )?.name as Name;
    const travelerOneName = Object.values(booking.passengers ?? {})[0]
      ?.name as Name;
    hideContact =
      primaryContactName && travelerOneName
        ? isEqual(primaryContactName, travelerOneName)
        : false;
    return hideContact;
  },
);

export const selectBookingPrimaryContactEmail = createSelector(
  selectBookingPrimaryContact,
  (contact) => contact?.emailAddress,
);

export const selectBookingBags = createSelector(selectBooking, (booking) => {
  if (!booking) return;

  return booking.bags?.map((b) => {
    const journey = booking.journeys.find((j) => j.journeyKey === b.journeyKey);

    return {
      ...b,
      designator: journey?.designator,
      productClass: <CabinProductClass>journey?.productClass,
    } as JourneyBags;
  });
});

export const selectJourneyBagsCanSell = createSelector(
  selectBookingBags,
  selectJourneyDetails,
  selectJourneysInCheckInWindow,
  selectIsCheckInPath,
  (bookingBags, journeyDetails, checkInJourneys, isCheckInPath) => {
    if (!bookingBags || !journeyDetails) return;

    return bookingBags.filter(
      (bags) =>
        journeyDetails
          .filter((d) => d.canSellBags)
          .some((j) => j.journeyKey === bags.journeyKey) &&
        (isCheckInPath
          ? !!checkInJourneys?.some((j) => j.journeyKey === bags.journeyKey)
          : true),
    );
  },
);

export const selectBookingBagsTotalCost = createSelector(
  selectBookingTripBreakdown,
  (breakdown) => breakdown?.categoryTotals.bags?.totalPrice ?? 0,
);

export const selectBookingPaidBagsTotalCost = createSelector(
  selectBookingBags,
  (bags) =>
    bags?.reduce((bagsTotal, journeyBag) => {
      return (
        bagsTotal +
        journeyBag.passengerBags.reduce((journeyBagsTotal, passengerBag) => {
          return (
            journeyBagsTotal +
            passengerBag.bags.reduce((passengerBagsTotal, bag) => {
              return (
                passengerBagsTotal +
                bag.ssrs.reduce((bagSsrTotal, ssr) => {
                  return bagSsrTotal + (ssr.paid ? ssr.price : 0);
                }, 0)
              );
            }, 0)
          );
        }, 0)
      );
    }, 0) ?? 0,
);

export const selectBookingExtras = createSelector(selectBooking, (booking) => {
  return booking?.extras;
});

export const selectJourneysCanSellExtras = createSelector(
  selectJourneys,
  selectJourneyDetails,
  selectJourneysInCheckInWindow,
  selectIsCheckInPath,
  (journeys, journeyDetails, checkInJourneys, isCheckInPath) => {
    if (!journeys || !journeyDetails) return;

    return journeys?.filter(
      (journey) =>
        journeyDetails
          ?.filter((detail) => detail.canSellExtras)
          .some((j) => j.journeyKey === journey.journeyKey) &&
        (isCheckInPath
          ? !!checkInJourneys?.some((j) => j.journeyKey === journey.journeyKey)
          : true),
    );
  },
);

export const selectJourneyExtrasCanSell = createSelector(
  selectBookingExtras,
  selectJourneyDetails,
  (bookingExtras, journeyDetails) => {
    if (!bookingExtras || !journeyDetails) return;

    return bookingExtras?.filter(
      (extras) =>
        journeyDetails
          ?.filter((detail) => detail.canSellExtras)
          .some((j) => j.journeyKey === extras.journeyKey),
    );
  },
);

export const selectBookingPaidExtrasTotalCost = createSelector(
  selectBookingExtras,
  (extras) =>
    extras?.reduce((extrasTotal, journeyBag) => {
      return (
        extrasTotal +
        journeyBag.passengerExtras.reduce(
          (journeyExtrasTotal, passengerExtra) => {
            return (
              journeyExtrasTotal +
              passengerExtra.extras.reduce((passengerExtrasTotal, extra) => {
                return (
                  passengerExtrasTotal +
                  extra.ssrs.reduce((extraSsrTotal, ssr) => {
                    return extraSsrTotal + (ssr.paid ? ssr.price : 0);
                  }, 0)
                );
              }, 0)
            );
          },
          0,
        )
      );
    }, 0) ?? 0,
);

export const selectBookingExtrasTotalCost = createSelector(
  selectBookingTripBreakdown,
  (breakdown) => breakdown?.categoryTotals.extras?.totalPrice ?? 0,
);

export const selectBookingOtherFees = createSelector(
  selectBooking,
  (booking) => {
    return booking?.other;
  },
);

export const selectBookingOtherFeesTotalCost = createSelector(
  selectBookingTripBreakdown,
  (breakdown) => breakdown?.categoryTotals.other?.totalPrice ?? 0,
);

export const selectBookingOtherFeesTotalCostRounded = createSelector(
  selectBookingOtherFeesTotalCost,
  Math.ceil,
);

export const selectBookingSpoilageFees = createSelector(
  selectBooking,
  (booking) => {
    return booking?.spoilage;
  },
);

export const selectBookingSpoilageFeesTotalCost = createSelector(
  selectBookingTripBreakdown,
  (breakdown) => breakdown?.categoryTotals.spoilage?.totalPrice ?? 0,
);

export const selectBookingSpoilageFeesTotalCostRounded = createSelector(
  selectBookingSpoilageFeesTotalCost,
  Math.ceil,
);

export const selectBookingPayments = createSelector(
  selectBooking,
  (booking) => {
    return booking?.payments ?? [];
  },
);

export const selectOriginalBookingPayments = createSelector(
  selectOriginalBooking,
  (booking) => {
    return booking?.payments ?? [];
  },
);

export const selectBookingPendingPayments = createSelector(
  selectBookingPayments,
  (payments) => {
    return payments.filter((payment) => {
      return (
        payment.status === BookingPaymentStatus.Pending ||
        payment.status === BookingPaymentStatus.PendingCustomerAction
      );
    });
  },
);

export const selectBookingHasPendingPayments = createSelector(
  selectBookingPendingPayments,
  (pendingPayments) => {
    return pendingPayments.length > 0;
  },
);

export const selectBookingPendingCreditCardPayments = createSelector(
  selectBookingPendingPayments,
  (pendingPayments) => {
    return pendingPayments.filter((payment) => {
      return ![
        PaymentCodes.Agency,
        PaymentCodes.Cash,
        PaymentCodes.CreditFile,
        PaymentCodes.CreditShell,
        PaymentCodes.Other,
        PaymentCodes.Voucher,
        PaymentCodes.VoucherAdjustment,
      ].includes(payment.code);
    });
  },
);

export const selectBookingHasPendingCreditCardPayments = createSelector(
  selectBookingPendingCreditCardPayments,
  (pendingPayments) => {
    return pendingPayments.length > 0;
  },
);

export const selectOriginalBookingVoucherPayments = createSelector(
  selectOriginalBookingPayments,
  (payments) => {
    return payments.filter((payment) => payment.code === PaymentCodes.Voucher);
  },
);

export const selectBookingVoucherPayments = createSelector(
  selectBookingPayments,
  (payments) => {
    return payments.filter((payment) => payment.code === PaymentCodes.Voucher);
  },
);

export const selectOriginalBookingCreditFilePayments = createSelector(
  selectOriginalBookingPayments,
  (payments) => {
    return payments.filter(
      (payment) => payment.code === PaymentCodes.CreditFile,
    );
  },
);

export const selectBookingCreditFilePayments = createSelector(
  selectBookingPayments,
  (payments) => {
    return payments.filter(
      (payment) => payment.code === PaymentCodes.CreditFile,
    );
  },
);

export const selectBookingTotalAmountPaid = createSelector(
  selectBookingTripBreakdown,
  (breakdown) => breakdown?.paymentsTotal ?? 0,
);

export const selectBookingTotalAmountPaidRounded = createSelector(
  selectBookingTotalAmountPaid,
  Math.ceil,
);

export const selectBookingTotalToCollect = createSelector(
  selectBookingTripBreakdown,
  (breakdown) => breakdown?.totalToCollect ?? 0,
);

export const selectBookingTotalToCollectRounded = createSelector(
  selectBookingTotalToCollect,
  Math.ceil,
);

export const selectBookingFaresTotalPriceTripSummary = createSelector(
  selectBookingTripBreakdown,
  (breakdown) => breakdown?.categoryTotals.fares?.totalPrice ?? 0,
);

export const selectBookingFaresTotalPriceTripSummaryRounded = createSelector(
  selectBookingFaresTotalPriceTripSummary,
  Math.ceil,
);

export const selectBookingFaresStrikethroughTotalPriceTripSummary =
  createSelector(
    selectBookingTripBreakdown,
    (breakdown) => breakdown?.categoryTotals.fares?.strikethroughTotalPrice ?? 0,
  );

export const selectBookingFaresStrikethroughTotalPriceTripSummaryRounded =
  createSelector(
    selectBookingFaresStrikethroughTotalPriceTripSummary,
    Math.ceil,
  );

export const selectBookingBalanceDueStrikethrough =
  createSelector(
    selectBookingTripBreakdown,
    (breakdown) => breakdown?.balanceDueStrikethrough,
  );


export const selectBookingBalanceDueStrikethroughRounded =
  createSelector(
    selectBookingBalanceDueStrikethrough,
    Math.ceil,
  );


export const selectBookingMeals = createSelector(
  selectBooking,
  (booking) => booking?.meals,
);

export const selectBookingMealsTotalCost = createSelector(
  selectBookingTripBreakdown,
  (breakdown) => breakdown?.categoryTotals.meals?.totalPrice ?? 0,
);

/* istanbul ignore next  */
export const selectBookingSeats = createSelector(
  selectBooking,
  (booking) => booking?.seats,
);

export const selectOriginalBookingSeats = createSelector(
  selectOriginalBooking,
  (originalBooking) => originalBooking?.seats,
);

export const selectOriginalBookingSeatsBySegmentAndPassenger = createSelector(
  selectOriginalBookingSeats,
  (originalBookingSeats) => {
    return originalBookingSeats?.reduce(
      (seatsBySegment, segmentSeats) => {
        return {
          ...seatsBySegment,
          [segmentSeats.segmentKey]: segmentSeats.passengerSeats.reduce(
            (seatsByPax, paxSeat) => {
              return {
                ...seatsByPax,
                [paxSeat.passengerKey]: paxSeat,
              };
            },
            {} as { [paxKey: string]: PassengerSeat },
          ),
        };
      },
      {} as {
        [segmentKey: string]: {
          [paxKey: string]: PassengerSeat;
        };
      },
    );
  },
);

export const selectOriginalBookingProductClassByJourney = createSelector(
  selectOriginalBooking,
  (originalBooking) =>
    originalBooking?.journeys?.reduce(
      (pcByJourney, journey) => {
        return {
          ...pcByJourney,
          [journey.journeyKey]: journey.productClass,
        };
      },
      {} as { [key: string]: string },
    ) ?? {},
);

export const selectProductClassByJourney = createSelector(
  selectBooking,
  (booking) =>
    booking?.journeys?.reduce(
      (pcByJourney, journey) => {
        return {
          ...pcByJourney,
          [journey.journeyKey]: journey.productClass,
        };
      },
      {} as { [key: string]: string },
    ) ?? {},
);

export const selectIsInCheckinWindowByJourney = createSelector(
  selectBookingDetails,
  (details) =>
    details?.journeyDetail?.reduce(
      (pcByJourney, journey) => {
        return {
          ...pcByJourney,
          [journey.journeyKey]: journey.inCheckinWindow,
        };
      },
      {} as { [key: string]: boolean },
    ) ?? {},
);

export const selectBookingIsWithinDot = createSelector(
  selectBookingDetails,
  (details) => {
    return details?.isWithinDot ?? false;
  },
);

export const selectBookingIsDohop = createSelector(
  selectBookingDetails,
  (details) => {
    return details?.isDohop ?? false;
  },
);

export const selectBookingCurrencyCode = createSelector(
  selectBookingCurrency,
  selectSelectedCurrencyCode,
  (bookingCurrency, selectedCurrency) => bookingCurrency ?? selectedCurrency,
);

export const selectBookingCurrencyLocale = createSelector(
  selectBookingCurrencyCode,
  (currency) => {
    switch (currency) {
      case CURRENCY_EUR:
        return CULTURE_FRENCH;
      case CURRENCY_NOK:
        return CULTURE_NORWAY;
      case CURRENCY_GBP:
        return CULTURE_GREAT_BRITAIN;
      default:
        return FALLBACK_CULTURE;
    }
  },
);

const mealServicesCountByPassengerAndSsrCode = (
  passengerKey: string,
  ssrCodes: string[],
  meals: Meals[],
  service: MealServiceType,
): { [ssrCode: string]: number } => {
  return ssrCodes.reduce((mealsBySsr, mealSsrCode) => {
    return {
      ...mealsBySsr,
      [mealSsrCode]: meals
        ?.reduce((ssrMeals, meal) => {
          const matchingMeals = meal.passengerMeals
            .filter((pm) => pm.passengerKey === passengerKey)
            .reduce((ssrServices, passengerMeal) => {
              const matchingServices = passengerMeal[service].filter(
                (serviceMeal) => serviceMeal.ssrCode === mealSsrCode,
              );
              return ssrServices.concat(matchingServices);
            }, [] as MealService[]);
          return ssrMeals.concat(matchingMeals);
        }, [] as MealService[])
        .reduce((totalCount, mealService) => {
          return totalCount + mealService.totalCount;
        }, 0),
    };
  }, {});
};

export const selectBookingMealsCountByPassengerAndSsr = createSelector(
  selectBookingMeals,
  selectPassengers,
  (meals, passengers) => {
    if (!passengers || !meals) {
      return {};
    }
    return Object.keys(passengers).reduce(
      (
        byPassenger: { [key: string]: PassengerWithMealsCountBySsr },
        passengerKey: string,
      ) => {
        return {
          ...byPassenger,
          [passengerKey]: {
            passengerKey,
            firstService: mealServicesCountByPassengerAndSsrCode(
              passengerKey,
              Object.values(MealFirstServiceSsrEnum),
              meals,
              MealServiceType.FIRST_SERVICE,
            ),
            secondService: mealServicesCountByPassengerAndSsrCode(
              passengerKey,
              Object.values(MealSecondServiceSsrEnum),
              meals,
              MealServiceType.SECOND_SERVICE,
            ),
          },
        };
      },
      {},
    );
  },
);

export const selectBookingExtrasCountByPassengerAndSsr = createSelector(
  selectBookingExtras,
  selectPassengers,
  (extras, passengers) => {
    if (!passengers) {
      return {};
    }
    return Object.keys(passengers).reduce(
      (
        byPassenger: { [key: string]: PassengerWithExtrasCountBySsr },
        passengerKey: string,
      ) => {
        return {
          ...byPassenger,
          [passengerKey]: {
            passengerKey,
            extras: Object.values(ExtrasSsrsEnum).reduce(
              (extrasBySsr, extrasSsrCode) => {
                return {
                  ...extrasBySsr,
                  [extrasSsrCode]: extras
                    ?.reduce((ssrExtras, extra) => {
                      const matchingPassengerExtras = extra.passengerExtras
                        .filter((pe) => pe.passengerKey === passengerKey)
                        .reduce(
                          (curentMatchingPassengerExtras, passengerExtra) => {
                            const matchingExtras = passengerExtra.extras.filter(
                              (extra) => extra.ssrCode === extrasSsrCode,
                            );
                            return curentMatchingPassengerExtras.concat(
                              matchingExtras,
                            );
                          },
                          [] as PassengerSsr[],
                        );
                      return ssrExtras.concat(matchingPassengerExtras);
                    }, [] as PassengerSsr[])
                    .reduce((totalCount, extra) => {
                      return totalCount + extra.totalCount;
                    }, 0),
                };
              },
              {},
            ),
          },
        };
      },
      {},
    );
  },
);

export const selectBookingBagsCountByPassenger = createSelector(
  selectBookingBags,
  selectPassengers,
  (bags, passengers) => {
    if (!passengers) {
      return {};
    }
    return Object.keys(passengers).reduce(
      (byPassenger: { [key: string]: number }, passengerKey: string) => {
        return {
          ...byPassenger,
          [passengerKey]:
            bags?.reduce((passengerBagsTotal, bag) => {
              return (
                passengerBagsTotal +
                bag.passengerBags
                  .filter((pb) => pb.passengerKey === passengerKey)
                  .reduce(
                    (totalCount, pb) =>
                      totalCount +
                      pb.bags.reduce(
                        (totalInner, b) => totalInner + b.totalCount,
                        0,
                      ),
                    0,
                  )
              );
            }, 0) ?? 0,
        };
      },
      {},
    );
  },
);

export const selectBookingBagsTotalCount = createSelector(
  selectBookingTripBreakdown,
  (breakdown) =>
    calculateBookingCategoryTotalCount(
      breakdown?.categoryTotals?.bags?.details,
    ),
);

export const selectBookingMealsTotalCount = createSelector(
  selectBookingTripBreakdown,
  (breakdown) =>
    calculateBookingCategoryTotalCount(
      breakdown?.categoryTotals?.meals?.details,
    ),
);

export const selectBookingExtrasTotalCount = createSelector(
  selectBookingTripBreakdown,
  (breakdown) =>
    calculateBookingCategoryTotalCount(
      breakdown?.categoryTotals?.extras?.details,
    ),
);

export const selectBookingSeatsTotalCount = createSelector(
  selectBookingTripBreakdown,
  (breakdown) =>
    calculateBookingCategoryTotalCount(
      breakdown?.categoryTotals?.seats?.details,
    ),
);

export const calculateBookingCategoryTotalCount = (
  details: N0CategoryDetail[] | undefined,
): number =>
  details?.reduce((byPassengerCount: number, detail: N0CategoryDetail) => {
    return byPassengerCount + detail.totalCount;
  }, 0) ?? 0;

export const selectAllSeatsByPassenger = createSelector(
  selectBookingSeats,
  selectPassengers,
  (seats, passengers) => {
    if (!passengers) {
      return {};
    }
    return Object.values(passengers).reduce(
      (
        byPassenger: { [key: string]: PassengerSeat[] },
        passenger: Passenger,
      ) => {
        return {
          ...byPassenger,
          [passenger.passengerKey]:
            seats?.reduce((passengerSeats, seat) => {
              return passengerSeats.concat(
                seat.passengerSeats.filter(
                  (ps) => ps.passengerKey === passenger.passengerKey,
                ),
              );
            }, [] as PassengerSeat[]) ?? [],
        };
      },
      {},
    );
  },
);

export const selectBookingSeatsTotalCost = createSelector(
  selectBookingTripBreakdown,
  (breakdown) => breakdown?.categoryTotals.seats?.totalPrice ?? 0,
);

const mealServicesCostAndCountBySsrCode = (
  ssrCodes: string[],
  meals: Meals[],
  service: MealServiceType,
): { [ssrCode: string]: CostAndCount } => {
  return ssrCodes.reduce((mealsBySsr, mealSsrCode) => {
    return {
      ...mealsBySsr,
      [mealSsrCode]: meals
        ?.reduce((ssrMeals, meal) => {
          const matchingMeals = meal.passengerMeals.reduce(
            (ssrServices, passengerMeal) => {
              const matchingServices = passengerMeal[service].filter(
                (serviceMeal) => serviceMeal.ssrCode === mealSsrCode,
              );
              return ssrServices.concat(matchingServices);
            },
            [] as MealService[],
          );
          return ssrMeals.concat(matchingMeals);
        }, [] as MealService[])
        .reduce(
          (totalCostAndCount, mealService) => {
            return {
              cost: totalCostAndCount.cost + mealService?.totalPrice,
              count: totalCostAndCount.count + mealService.totalCount,
            };
          },
          {
            cost: 0,
            count: 0,
          } as CostAndCount,
        ),
    };
  }, {});
};

export const selectBookingMealsCostAndCountBySsr = createSelector(
  selectBookingMeals,
  (meals) => {
    if (!meals) {
      return {} as MealsCostAndCountBySsr;
    }
    return {
      firstService: mealServicesCostAndCountBySsrCode(
        Object.values(MealFirstServiceSsrEnum),
        meals,
        MealServiceType.FIRST_SERVICE,
      ),
      secondService: mealServicesCostAndCountBySsrCode(
        Object.values(MealSecondServiceSsrEnum),
        meals,
        MealServiceType.SECOND_SERVICE,
      ),
    } as MealsCostAndCountBySsr;
  },
);

export const selectBookingExtrasCostAndCountBySsr = createSelector(
  selectBookingExtras,
  (extras) => {
    return Object.values(ExtrasSsrsEnum).reduce(
      (extrasBySsr, extrasSsrCode) => {
        return {
          ...extrasBySsr,
          [extrasSsrCode]: extras
            ?.reduce((ssrExtras, extra) => {
              const matchingPassengerExtras = extra.passengerExtras.reduce(
                (curentMatchingPassengerExtras, passengerExtra) => {
                  const matchingExtras = passengerExtra.extras.filter(
                    (extra) => extra.ssrCode === extrasSsrCode,
                  );
                  return curentMatchingPassengerExtras.concat(matchingExtras);
                },
                [] as PassengerSsr[],
              );
              return ssrExtras.concat(matchingPassengerExtras);
            }, [] as PassengerSsr[])
            .reduce(
              (totalCostAndCount, extra) => {
                return {
                  cost: totalCostAndCount.cost + extra?.totalPrice,
                  count: totalCostAndCount.count + extra.totalCount,
                };
              },
              {
                cost: 0,
                count: 0,
              } as CostAndCount,
            ),
        };
      },
      {},
    ) as ExtrasCostAndCountBySsr;
  },
);

export const selectBookingBagsCostAndCountBySsr = createSelector(
  selectBookingBags,
  (bags) => {
    return Object.values(BagsSsrEnum).reduce((bagsBySsr, bagsSsrCode) => {
      return {
        ...bagsBySsr,
        [bagsSsrCode]: bags
          ?.reduce((ssrBags, bag) => {
            const matchingPassengerBags = bag.passengerBags.reduce(
              (curentMatchingPassengerBags, passengerBag) => {
                const matchingBags = passengerBag.bags.filter(
                  (bag) => bag.ssrCode === bagsSsrCode,
                );
                return curentMatchingPassengerBags.concat(matchingBags);
              },
              [] as PassengerSsr[],
            );
            return ssrBags.concat(matchingPassengerBags);
          }, [] as PassengerSsr[])
          .reduce(
            (totalCostAndCount, bag) => {
              return {
                cost: totalCostAndCount.cost + bag?.totalPrice,
                count: totalCostAndCount.count + bag.totalCount,
              };
            },
            {
              cost: 0,
              count: 0,
            } as CostAndCount,
          ),
      };
    }, {}) as BagsCostAndCountBySsr;
  },
);

export const selectBookingFaresAndCountByPassengerType = createSelector(
  selectSegments,
  (segments) => {
    const fares = segments?.reduce(
      (innerFares, segment) => innerFares.concat(segment.fares ?? []),
      [] as SegmentFare[],
    );
    const passengerFares = fares?.reduce(
      (innerFares, fare) => innerFares.concat(fare.passengerFares),
      [] as PassengerFare[],
    );
    return Object.values(PassengerTypeCode).reduce(
      (fareAndCountByPassengerType, passengerType) => {
        const thesePassengerFares =
          passengerFares?.filter((pf) => pf.passengerType === passengerType) ??
          [];
        const serviceCharges = thesePassengerFares
          .reduce(
            (charges, pf) =>
              charges.concat(pf.serviceCharges as ServiceCharge[]),
            [] as ServiceCharge[],
            // charge code null is non-tax charges only (fares)
          )
          .filter((charge) => charge.code === null);
        return {
          ...fareAndCountByPassengerType,
          [passengerType]: {
            cost: serviceCharges.reduce(
              (sumCharges, charge) => sumCharges + charge.amount,
              0,
            ),
            count: thesePassengerFares.length,
          } as CostAndCount,
        };
      },
      {} as { [key in PassengerTypeCode]: CostAndCount },
    );
  },
);

export const selectBookingFareTaxesByTaxType = createSelector(
  selectSegments,
  (segments) => {
    const fares = segments?.reduce(
      (innerFares, segment) => innerFares.concat(segment.fares ?? []),
      [] as SegmentFare[],
    );
    const passengerFares =
      fares?.reduce(
        (innerFares, fare) => innerFares.concat(fare.passengerFares),
        [] as PassengerFare[],
      ) ?? [];
    const serviceCharges = passengerFares.reduce(
      (charges, pf) => charges.concat(pf.serviceCharges as ServiceCharge[]),
      [] as ServiceCharge[],
    );
    return Object.values(TaxTypeCode).reduce(
      (fareAndCountByTaxType, taxType) => {
        const taxServiceCharges = serviceCharges.filter(
          (charge) => charge.code === taxType,
        );
        return {
          ...fareAndCountByTaxType,
          [taxType]: taxServiceCharges,
        };
      },
      {} as { [key in TaxTypeCode]: ServiceCharge[] },
    );
  },
);

export const countDeclinedPayments = (payments: Payment[]): number => {
  return payments.reduce((totalRunningDeclinedPayments, payment) => {
    if (
      payment.status === BookingPaymentStatus.Declined &&
      (payment.amounts?.amount ?? 0) > 0
    ) {
      return totalRunningDeclinedPayments + 1;
    }
    return totalRunningDeclinedPayments;
  }, 0);
};

export const selectBookingHasReachedMaxConsecutiveDeclinedPayments =
  createSelector(
    selectOriginalBookingPayments,
    selectBookingPayments,
    (originalPayments, payments) => {
      const originalDeclinedPayments = countDeclinedPayments(originalPayments);
      const currentDeclinedPayments = countDeclinedPayments(payments);
      const newDeclinedPaymentsCount =
        currentDeclinedPayments - originalDeclinedPayments;
      return newDeclinedPaymentsCount >= MAX_CONSECUTIVE_DECLINED_PAYMENTS;
    },
  );

export const selectBookingAreAllPassengersAssignedSeatsOnSegmentsInCheckinWindow =
  createSelector(
    selectSegmentsWithJourneyKeys,
    selectIsInCheckinWindowByJourney,
    selectBookingSeats,
    selectPassengers,
    (segments, isInCheckinWindowByJourney, seats, passengers) => {
      if (!passengers) {
        return false;
      }
      const segmentsThatWillAutoAssignSeats =
        segments?.filter(
          (segment) => isInCheckinWindowByJourney[segment.journeyKey],
        ) ?? [];
      return segmentsThatWillAutoAssignSeats.every((segment) => {
        return Object.keys(passengers).every((paxKey) => {
          return seats?.find((seat) => {
            return (
              seat.segmentKey === segment.segmentKey &&
              seat.passengerSeats.find((paxSeat) => {
                return paxSeat.passengerKey === paxKey;
              })
            );
          });
        });
      });
    },
  );

export const selectBookingCheckInBoardingPasses = createSelector(
  selectBookingCheckIn,
  (checkIn) => checkIn?.boardingPasses,
);

export const selectBookingCheckInBoardingPassesHasEveryFailiture =
  createSelector(selectBookingCheckInBoardingPasses, (boardingPasses) => {
    if (!boardingPasses) {
      return false;
    }

    return boardingPasses.boardingPasses.every((boardingPass) => {
      return !!boardingPass?.failureReasons?.length;
    });
  });

export const selectBookingCheckInBoardingPassesHasNoEveryFailiture =
  createSelector(selectBookingCheckInBoardingPasses, (boardingPasses) => {
    return (
      !boardingPasses ||
      !boardingPasses.boardingPasses.every((boardingPass) => {
        return !!boardingPass?.failureReasons?.length;
      })
    );
  });

/* istanbul ignore next  */
export const selectBookingPlusgradeResponse = createSelector(
  selectBookingFeatureState,
  (state) => state.plusgrade,
);

/* istanbul ignore next  */
export const selectDisruptionCheckResponse = createSelector(
  selectBookingFeatureState,
  (state) => state.disruption,
);

export const selectBookingCanShowLoanPayment = createSelector(
  selectPassengersList,
  selectBookingCurrency,
  (passengers, currency) => {
    const adults = passengers?.filter(
      (p) =>
        (p.passengerTypeCode === PassengerTypeCode.ADT &&
          p.addresses?.some((a) => a.countryCode === COUNTRY_CODE_US)) ||
        (p.passengerTypeCode === PassengerTypeCode.OTN &&
          p.addresses?.some((a) => a.countryCode === COUNTRY_CODE_US)),
    );
    return currency === CURRENCY_USD && adults ? adults.length > 0 : false;
  },
);

export const selectPromoCodeApplied = createSelector(
  selectSegments,
  (segments: Segment[] | null) => {
    if (!segments) {
      return false;
    }

    const serviceCharges = segments.flatMap((segment) => {
      return segment.fares?.flatMap((fares) =>
        fares.passengerFares.flatMap(
          (passengerFares) => passengerFares.serviceCharges,
        ),
      );
    });

    return serviceCharges.some(
      (serviceCharge) => serviceCharge?.type === ChargeType.PromotionDiscount,
    );
  },
);

export const selectBookingCheckInDocCheck = createSelector(
  selectBookingFeatureState,
  (state) => state.checkInDocCheck,
);

export const selectBookingDetailsCanEditPassports = createSelector(
  selectBookingDetails,
  (details) => details?.canEditPassports,
)

export const selectBookingHasActiveError = createSelector(
  selectBookingFeatureState,
  (state) => state.hasActiveBookingError,
);

export const selectJourneySummary = createSelector(
  selectOriginalBooking,
  selectBooking,
  (originalBooking, currentBooking): JourneySummary => {
    const originalJourneyBreakdowns =
      originalBooking?.tripBreakdown.journeyBreakdowns;
    const currentJourneyBreakdowns =
      currentBooking?.tripBreakdown.journeyBreakdowns;

    if (!currentJourneyBreakdowns) {
      return {
        journeyBreakdown: [],
        journeyBreakdownObject: {},
        journeyTotal: 0,
        journeyType: BookingType.GENERIC,
      };
    }

    const journeyBreakdownsDifference: N0JourneyBreakdown[] =
      currentJourneyBreakdowns.map((currentJourney) => {
        
        const originalJourney = originalJourneyBreakdowns?.find(
          (originalJourney) =>
            originalJourney.journeyKey === currentJourney.journeyKey,
        );

        if (!originalJourney) {
          return { ...currentJourney } as N0JourneyBreakdown;
        }

        const currentCategoryBreakdown = {
          ...currentJourney.categoryBreakdown,
        };
        const originalCategoryBreakdown = {
          ...originalJourney.categoryBreakdown,
        };

        const categoryKeys = Object.keys(currentCategoryBreakdown);

        const updatedCategoryBreakdown = categoryKeys.reduce(
          (acc, key) => {
            const currentCategory = currentCategoryBreakdown[key];
            const originalCategory = originalCategoryBreakdown[key];

            if (!currentCategory || !originalCategory) {
              return { ...acc };
            }

            const currentCategoryDetails = currentCategory.details;
            const originalCategoryDetails = originalCategory.details;

            if (currentCategoryDetails && originalCategoryDetails) {
              const updatedPrice =
                currentCategory.totalPrice - originalCategory.totalPrice;
              if (updatedPrice === 0) {
                return { ...acc };
              }

              const updatedDetails = currentCategoryDetails
                .map((detail) => {
                  const originalDetail = originalCategoryDetails.find(
                    (originalDetail) => originalDetail.code === detail.code,
                  );

                  const totalPrice =
                    detail.totalPrice - (originalDetail?.totalPrice || 0);
                  if (totalPrice === 0) {
                    return null;
                  }

                  if (!originalDetail) {
                    return { ...detail };
                  }

                  return {
                    ...detail,
                    totalCount: detail.totalCount - originalDetail.totalCount,
                    totalPrice: totalPrice,
                  };
                })
                .filter((detail) => detail !== null);

              return {
                ...acc,
                [key]: {
                  ...currentCategory,
                  details: updatedDetails,
                  totalPrice: updatedPrice,
                } as N0CategoryBreakdown
              };
            }

            const currentCategoryFareDetails = currentCategory.fareDetails;
            const originalCategoryFareDetails = originalCategory.fareDetails;

            if (currentCategoryFareDetails && originalCategoryFareDetails) {
              const updatedFareDetails = currentCategoryFareDetails
                .map((fareDetail) => {
                  const originalFareDetail = originalCategoryFareDetails.find(
                    (originalFareDetail) =>
                      originalFareDetail.passengerType ===
                      fareDetail.passengerType,
                  );

                  if (!originalFareDetail) {
                    return { ...fareDetail };
                  }

                  const totalPrice =
                    fareDetail.totalPrice - originalFareDetail.totalPrice;
                  if (totalPrice === 0) {
                    return null;
                  }

                  return {
                    ...fareDetail,
                    totalPrice: totalPrice,
                  };
                })
                .filter((fareDetail) => fareDetail !== null);

              if (!updatedFareDetails.length) {
                return { ...acc };
              }

              const updatedPrice =
                currentCategoryFareDetails.totalPrice -
                originalCategoryFareDetails.totalPrice;

              return {
                ...acc,
                [key]: {
                  ...currentCategory,
                  fareDetails: updatedFareDetails,
                  totalPrice: updatedPrice,
                },
              };
            }

            const priceDifference =
              currentCategory.totalPrice - originalCategory.totalPrice;
            if (priceDifference === 0) {
              return { ...acc };
            }

            return {
              ...acc,
              [key]: {
                ...currentCategory,
                totalPrice: priceDifference,
              },
            };
          },
          {} as typeof currentCategoryBreakdown,
        );

        const priceDifference =
          (currentJourney?.journeyTotal || 0) -
          (originalJourney?.journeyTotal || 0);
        const { journeyTotalStrikethrough, ...restOfJourney } = currentJourney;

        return {
          ...restOfJourney,
          categoryBreakdown: updatedCategoryBreakdown,
          journeyTotal: priceDifference,
        } as N0JourneyBreakdown;
      });

    const journeyTotal = journeyBreakdownsDifference.reduce(
      (total, journey) => total + (journey.journeyTotal || 0),
      0,
    );

    const journeyBreakdown = journeyBreakdownsDifference.map((journey) => ({
      categoryBreakdown: journey.categoryBreakdown,
      journeyKey: journey.journeyKey,
      journeyTotal: journey.journeyTotal || 0,
      journeyTotalStrikethrough: journey.journeyTotalStrikethrough || 0,
    }));

    const journeyType = () => {
      const allCategoryKeys = journeyBreakdownsDifference.flatMap((journey) =>
        Object.keys(journey.categoryBreakdown || {}),
      );

      const hasFareDetails = journeyBreakdownsDifference.some((journey) =>
        Object.values(journey.categoryBreakdown || {}).some(
          (category) => category.fareDetails && category.fareDetails.length > 0,
        ),
      );

      if (hasFareDetails) {
        return BookingType.INVALID;
      }

      const uniqueCategoryKeys = [...new Set(allCategoryKeys)];

      if (uniqueCategoryKeys.length === 0) {
        return BookingType.GENERIC;
      }

      if (uniqueCategoryKeys.length === 1) {
        const singleCategory = uniqueCategoryKeys[0];
        if (singleCategory === 'bags') return BookingType.BAGS;
        if (singleCategory === 'extras') return BookingType.EXTRAS;
        if (singleCategory === 'meals') return BookingType.MEALS;
        if (singleCategory === 'seats') return BookingType.SEATS;
      }

      return BookingType.GENERIC;
    };

    return {
      journeyBreakdown,
      journeyBreakdownObject: {
        ...journeyBreakdown.reduce(
          (acc, journey) => {
            acc[journey.journeyKey] = journey;
            return acc;
          },
          {} as { [key: string]: N0JourneyBreakdown },
        ),
      },
      journeyTotal,
      journeyType: journeyType(),
    };
  },
);

